<template>
  <component
    :is="pageRoot"
    :style="colorVarStyles"
  >
    <LpuStaffAdmin />
    <CoreBread
      v-if="ff_lpu_branding_page_v4 && !isMobile"
      :items="breadcrumbs"
    />
    <div
      v-if="ff_lpu_branding_page_v4"
      class="d-flex flex-column container"
    >
      <PetitionBanner :root-class="isMobile ? 'mb-4 px-3' : 'mb-6'" />
      <AwardBanner :class="{ 'pb-4 mt-n3': isMobile }" />
    </div>
    <div
      data-qa="lpu_page"
      class="lpu-branding position-relative"
      :class="{
        container: ff_lpu_branding_page_v4,
        'lpu-branding--mobile': isMobile,
        'mb-5': ff_lpu_branding_page_v4 && !isMobile,
        'pt-4': ff_lpu_branding_page_v4 && isMobile,
      }"
      :style="ff_lpu_branding_page_v4 ? undefined : colorVarStyles"
      itemtype="https://schema.org/LocalBusiness"
      itemscope
    >
      <meta
        v-if="ff_lpu_branding_page_v4"
        itemprop="name"
        :content="lpuName"
      >
      <meta
        v-if="ff_lpu_branding_page_v4 && localBusinessItemprops.telephone"
        itemprop="telephone"
        :content="localBusinessItemprops.telephone"
      >
      <span
        v-if="ff_lpu_branding_page_v4 && (localBusinessItemprops.addressLocality || localBusinessItemprops.streetAddress)"
        itemprop="address"
        itemscope
        itemtype="https://schema.org/PostalAddress"
      >
        <meta
          v-if="localBusinessItemprops.addressLocality"
          itemprop="addressLocality"
          :content="localBusinessItemprops.addressLocality"
        >
        <meta
          v-if="localBusinessItemprops.streetAddress"
          itemprop="streetAddress"
          :content="localBusinessItemprops.streetAddress"
        >
      </span>
      <meta
        v-if="ff_lpu_branding_page_v4 && localBusinessItemprops.priceRange"
        itemprop="priceRange"
        :content="localBusinessItemprops.priceRange"
      >
      <meta
        v-if="ff_lpu_branding_page_v4 && localBusinessItemprops.logo"
        itemprop="logo"
        :content="localBusinessItemprops.logo"
      >
      <PreviewMenu
        v-if="context.request.feature_flags.ff_lpu_branding_page_v2 && context.is_lpu_branding_preview"
        :class="{ 'order-first': context.request.feature_flags.ff_lpu_branding_page_v4 }"
        :lpu-id="context.lpu.id"
      />
      <div
        class="d-flex flex-column ga-6"
        :class="sectionOrder.card"
      >
        <component
          :is="HeroSection"
          :lpu="context.lpu"
          :lpu-branding="context.lpu_branding"
          :lpu-video-card="context.lpu_video_card"
          :synonym-prices-count="context.synonym_prices_count"
        />
        <SectionMenu
          v-if="context.request.feature_flags.ff_lpu_branding_page_v2"
          :lpu="context.lpu"
          :synonym-prices-count="context.synonym_prices_count"
        />
      </div>
      <template
        v-if="context.request.feature_flags.ff_lpu_branding_page_v2"
      >
        <template v-if="context.request.feature_flags.ff_lpu_branding_page_v4">
          <LpuBanners
            v-if="context.lpu_branding?.banners?.length"
            :class="sectionOrder.banners"
            :banners="context.lpu_branding.banners"
          />
          <LpuSpecializations
            v-if="context.lpu_branding?.categories?.length"
            :class="sectionOrder.specializations"
            :specializations="context.lpu_branding.categories"
            :lpu="context.lpu"
          />
        </template>
        <div
          v-else-if="context.lpu_branding?.banners?.length || context.lpu_branding?.categories?.length"
          class="d-flex flex-column gr-8"
        >
          <LpuBanners
            v-if="context.lpu_branding?.banners?.length"
            :banners="context.lpu_branding.banners"
          />
          <LpuSpecializations
            v-if="context.lpu_branding?.categories?.length"
            :specializations="context.lpu_branding.categories"
            :lpu="context.lpu"
          />
        </div>
        <LpuAdvantages
          v-if="context.lpu_branding?.advantages?.length"
          :class="sectionOrder.advantages"
          :advantages="context.lpu_branding?.advantages"
          :video-url="context.lpu_video_card?.video_path?.url"
          :video-uuid="context.lpu_video_card?.uuid"
          :preview-url="context.lpu_video_card?.preview_path?.url"
        />
      </template>
      <template
        v-if="context.request.feature_flags.ff_lpu_branding_page_v3"
      >
        <LpuFeatures
          v-if="context.lpu_information"
          :class="sectionOrder.features"
          :lpu-information="context.lpu_information"
        />
        <LpuDoctorsList
          :class="sectionOrder.doctors"
          :lpu="context.lpu"
        />
        <LpuDoctorsSchedule
          v-if="context.lpu.appointment_on && context.lpu.has_slots"
          :class="sectionOrder.work_schedule"
          :lpu="context.lpu"
        />
        <LpuRating
          v-if="showRatingSection"
          :class="sectionOrder.rating_and_rates"
          :lpu="context.lpu"
        />
        <LpuReviews
          :class="sectionOrder.rating_and_rates"
          :lpu="context.lpu"
        />
        <LpuPromotions
          :class="sectionOrder.promotions"
          :lpu="context.lpu"
        />
        <LpuPrices
          :class="sectionOrder.prices"
          :lpu="context.lpu"
        />
        <LpuInformation
          :class="sectionOrder.gallery"
          :lpu="context.lpu"
          :lpu-information="context.lpu_information"
        />
        <LpuDocuments
          v-if="context.lpu.lpu_documents?.length"
          :class="sectionOrder.documents"
          :documents="context.lpu.lpu_documents"
          :lpu-name="context.lpu.name"
        />
        <LpuRelatedClinics
          v-if="context.related_lpus?.length"
          :class="sectionOrder.related_clinics"
          :items="context.related_lpus"
        />
      </template>
      <template
        v-if="context.request.feature_flags.ff_lpu_branding_page_v4"
      >
        <LpuPhonebook
          :class="sectionOrder.phone_directory"
        />
        <LpuInterview
          :class="sectionOrder.manager_interview"
        />
      </template>
      <CoreSnackbar />
      <meta
        v-if="ff_lpu_branding_page_v4"
        itemprop="url"
        :content="itempropUrl"
      >
    </div>
    <BreadcrumbsJsonLd
      v-if="ff_lpu_branding_page_v4 && isMobile"
      :json="breadcrumbsJsonLd"
    />
    <BreadcrumbsJsonLd
      v-if="ff_lpu_branding_page_v4"
      :json="localBusinessJsonLd"
    />
    <LpuPopupMap />
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { CoreLayout } from 'src/layouts'
import CoreBread from 'src/core/CoreBread/CoreBread.vue'
import BreadcrumbsJsonLd from 'src/core/CoreBread/BreadcrumbsJsonLd.vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import CoreSnackbar from 'src/core/CoreSnackbar/CoreSnackbar.vue'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { LpuBrandingBlockId } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import useBrandingColors from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_composables/useBrandingColors'
import AwardBanner from 'src/components/common/AwardBanner/AwardBanner.vue'
import PetitionBanner from 'src/components/common/PetitionBanner/PetitionBanner.vue'
import LpuAdvantages from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuAdvantages/LpuAdvantages.vue'
import LpuBanners from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuBanners/LpuBanners.vue'
import LpuDoctorsList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/LpuDoctorsList.vue'
import LpuDoctorsSchedule from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/LpuDoctorsSchedule.vue'
import LpuDocuments from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDocuments/LpuDocuments.vue'
import LpuFeatures from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuFeatures/LpuFeatures.vue'
import LpuInformation from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/LpuInformation.vue'
import LpuInterview from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInterview/LpuInterview.vue'
import LpuPhonebook from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/LpuPhonebook.vue'
import LpuPopupMap from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPopupMap.vue'
import LpuPrices from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/LpuPrices.vue'
import LpuPromotions from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/LpuPromotions.vue'
import LpuRating from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/LpuRating.vue'
import shouldHideDoctorsRating from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/shouldHideDoctorsRating'
import LpuRelatedClinics from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRelatedClinics/LpuRelatedClinics.vue'
import LpuReviews from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/LpuReviews.vue'
import LpuSpecializations from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuSpecializations/LpuSpecializations.vue'
import LpuStaffAdmin from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuStaffAdmin/LpuStaffAdmin.vue'
import PreviewMenu from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/PreviewMenu/PreviewMenu.vue'
import SectionMenu from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/SectionMenu.vue'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getLocalBusinessItemprops, {
  buildLocalBusinessJsonLd,
  getLocalBusinessRatingValue,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getLocalBusinessItemprops'

const { context }: ContextStore = useContextStore()
const { reverseFull } = useURLStore()
const isMobile = useIsMobile()
const showRatingSection = computed(() => {
  const hideDoctorsRating = shouldHideDoctorsRating(
    context.request.feature_flags.change_calc_rating_on_laboratory,
    context.lpu.lputype.id,
    Boolean(context.request.user.is_staff),
  )

  return !context.lpu.closed
    && (
      Boolean(context.has_some_rates_rating)
      || (!hideDoctorsRating && Boolean(context.has_some_doctors_rating))
      || Boolean(context.lpu.has_fraud_mark)
      || Boolean(context.request.user.is_staff)
    )
})
const ff_lpu_branding_page_v4 = computed(() => (
  context.request.feature_flags.ff_lpu_branding_page_v4
))
const pageRoot = computed(() => (
  ff_lpu_branding_page_v4.value ? CoreLayout : 'div'
))
/** Название ЛПУ для LocalBusiness (meta itemprop=name в body). */
const lpuName = computed(() => tDynamic(context.lpu, 'name'))
/**
 * URL клиники для LocalBusiness (meta itemprop=url в body).
 * Сайт ЛПУ, иначе канонический URL страницы — как в meta-url.html.
 */
const itempropUrl = computed(() => (
  context.lpu.site
  || reverseFull({
    name: 'lpu',
    args: [context.town.translit, context.lpu.translit],
  })
))
/** telephone / address / priceRange / logo — паритет с legacy LocalBusiness. */
const localBusinessItemprops = computed(() => getLocalBusinessItemprops({
  lpu: context.lpu,
  townName: tDynamic(context.town, 'name'),
  addressText: context.lpu.addr?.[0]
    ? tDynamic(context.lpu.addr[0], 'address')
    : undefined,
  priceRange: context.price_range,
  currencyAbbreviation: context.currency?.abbreviation,
  country: context.request.country,
  httpHost: context.request.http_host,
  pageUrl: reverseFull({
    name: 'lpu',
    args: [context.town.translit, context.lpu.translit],
  }),
  ffUpdateLpuLogo: Boolean(context.request.feature_flags.ff_update_lpu_logo),
  tStatic,
}))
const localBusinessJsonLd = computed(() => JSON.stringify(buildLocalBusinessJsonLd({
  name: lpuName.value,
  url: itempropUrl.value,
  itemprops: localBusinessItemprops.value,
  ratingValue: context.lpu.allrates > 0
    ? getLocalBusinessRatingValue(context.lpu.stars)
    : undefined,
  ratingCount: context.lpu.allrates,
})).replace(/</g, '\\u003c'))
const sectionOrder = computed(() => {
  const sections: Partial<Record<LpuBrandingBlockId, string>> = {}

  if (!ff_lpu_branding_page_v4.value) {
    return sections
  }

  context.lpu_branding?.block_order?.forEach((section, index) => {
    sections[section] = `order-${index}`
  })

  return sections
})

const breadcrumbs = computed(() => {
  const items = [
    {
      name: context.request.country.is_russia ? 'ПроДокторов' : 'YDoc',
      href: reverseFull({ name: 'prodoctorov_main' }),
    },
    {
      name: tDynamic(context.town, 'name'),
      href: reverseFull({ name: 'town', args: [context.town.translit] }),
    },
    {
      name: tStatic('Лечебные учреждения'),
      href: reverseFull({ name: 'town_lpu', args: [context.town.translit] }),
    },
  ]

  if (!context.lpu.closed) {
    const typeNames = tDynamic(context.lpu.lputype, 'names')
    const typeName = typeNames
      ? typeNames.charAt(0).toUpperCase() + typeNames.slice(1)
      : ''

    items.push({
      name: typeName,
      href: reverseFull({
        name: 'lpulist',
        args: [context.town.translit, context.lpu.lputype.translit],
      }),
    })
  }

  return items
})

/**
 * JSON-LD хлебных крошек для mobile (как в django mobile breadcrumbs include).
 * Визуальные крошки на mobile не показываются.
 */
const breadcrumbsJsonLd = computed(() => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.value.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.href,
  })),
}).replace(/</g, '\\u003c'))

const HeroSectionDesktop = () => import(/* webpackMode: 'eager' */ 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/HeroSectionDesktop.vue')
const HeroSectionMobile = () => import(/* webpackMode: 'eager' */ 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/HeroSectionMobile.vue')
const HeroSection = computed(() => (isMobile.value ? HeroSectionMobile : HeroSectionDesktop))

const { colorVarStyles } = useBrandingColors(computed(() => context.lpu_branding))
</script>

<style lang="scss">
@import 'src/styles/index';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/colors';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/rounded';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/branding-btn';
@import 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_styles/section-title';

.lpu-branding {
  display: flex;
  flex-direction: column;
  row-gap: 60px;

  &--mobile {
    row-gap: 32px;
  }

  // атом-классы заканчиваются на 12
  @for $i from 13 through 14 {
    .order-#{$i} {
      order: $i;
    }
  }
}
</style>
